// MC-1733: single-flight, coalescing reload for a turbo-frame.
//
// The board broadcasts a content-less `refresh_frame` on every lease
// transition and every task write — 20–39 per minute on a busy board, one
// per open viewer (Boards::ColumnBroadcast). A column render can take longer
// than the gap between broadcasts, so a naive `frame.reload()` per broadcast
// was pathological: each new reload aborted the fetch in flight, but Puma
// kept rendering the aborted request to completion (holding a DB connection
// the whole time), so the column an operator was watching never repainted —
// the KOM/discovery H12 incident.
//
// This applies the discipline the sweeps already use
// (Sentinel::CoalesceSweepService, and MC-1723's dispatcher): at most one
// reload of a given frame at a time, and however many refreshes arrive while
// one is running, exactly one trailing reload follows. A refresh arriving
// mid-reload never cancels the reload in flight — it only records that one
// more is owed once the current one paints. So the server renders a column no
// more often than it can finish one, no render is discarded because its
// client went away, and the frame still converges on current content.
//
// State lives on the frame element via a WeakMap, so different columns (and
// the drawer timeline / attachments frames, which share the `refresh_frame`
// action and are covered by the same discipline deliberately) reload
// independently and concurrently, and the entry is collected with the frame.

const state = new WeakMap()

// `frame.reload()` (Turbo 8) returns a promise that settles when the frame
// finishes (re-)loading. We treat both fulfilment and rejection as "done": on
// a failed or timed-out fetch we still clear the in-flight flag, so the frame
// is never wedged with no way forward — the trailing reload (or the next
// refresh) retries and the frame recovers.
function run(frame) {
  const entry = state.get(frame)
  entry.reloading = true
  // Cleared as this reload starts: it re-fetches current server state, so
  // only refreshes that arrive *after* this point owe a further reload.
  entry.pending = false
  Promise.resolve()
    .then(() => frame.reload())
    .catch(() => {})
    .finally(() => {
      entry.reloading = false
      if (entry.pending) run(frame)
    })
}

// Reload `frame`, single-flight with coalescing. Safe to call on every
// broadcast: the first call starts a reload, calls arriving while it runs
// collapse into exactly one trailing reload once it paints.
export function coalescedReload(frame) {
  let entry = state.get(frame)
  if (!entry) {
    entry = { reloading: false, pending: false }
    state.set(frame, entry)
  }

  if (entry.reloading) {
    // Already reloading: don't start a second fetch and don't cancel the one
    // in flight — just record that one further reload is owed. Any number of
    // refreshes arriving during this reload collapse to that single trailing
    // reload.
    entry.pending = true
    return
  }

  run(frame)
}
